import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core'
import { animate, state, style, transition, trigger } from '@angular/animations'
import { ActionFailed, SelectableRow } from '@tokeet-frontend/tv3-platform'
import { TableType } from '@tv3/shared/empty-table/table-type'
import { UserStorage } from '@tokeet-frontend/tv3-platform'
import * as R from 'ramda'
import {
  addRentalImagesComplete,
  Channel,
  ChannelNameTokens,
  ConfirmDialogService,
  selectRentalCities,
  selectRentalTags,
  untilDestroy,
  updateRentalBaseRateComplete,
  updateRentalComplete,
  updateRentalDetailsComplete,
  updateRentalInstructionsComplete,
} from '@tokeet-frontend/tv3-platform'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { createSearch, isEmptyTable, localeCompareSort } from '@tokeet-frontend/tv3-platform'
import { select, Store } from '@ngrx/store'
import { BehaviorSubject, combineLatest } from 'rxjs'
import { debounceTime, filter, startWith } from 'rxjs/operators'
import { FormControl } from '@angular/forms'
import {
  ActivateHopperListing,
  ActivateHopperListingSuccess,
  CreateHopperListing,
  CreateHopperListingSuccess,
  DeactivateHopperListing,
  DeleteHopperListing,
  GetHopperListings,
  getHopperListings,
  GetHopperRentalValidations,
  HopperCancellationPolicies,
  HopperListingViewModel,
  ListingsValidationFieldResult,
  ListingValidationFieldResultItem,
  selectHopperRentalValidations,
  selectHopperValidationsLoaded,
  UpdateHopperListingSuccess,
} from '@tokeet-frontend/channels'
import { Actions, ofType } from '@ngrx/effects'
import { DeleteRoomComplete, UpdateRoomComplete, UpsertRoomsComplete } from '@tokeet-frontend/rentals'
import { LoadConnectionsByChannel, RemoveLocalConnectionsByIds } from '@tv3/store/connection/connection.actions'
import { RentalValidationFixHelperService } from '../../rental-validation-fix-helper.service'
import { orderBy, some, isNil, result } from 'lodash'
import { RentalDialogTab } from '@tv3/containers/rentals/overlays/rental-overlay/rental-overlay.component'
import { BookingFormulasOverlayService } from '@tv3/containers/formulas/overlay/booking-formulas-overlay.service'
import { getChannelConnectionId } from '@tv3/store/connection/connection.model'
import { SelectItemDialogService } from '@tv3/shared/dialogs/select-item-dialog/select-item-dialog.service'

@Component({
  selector: 'app-connect-hopper-wizard-step2',
  templateUrl: './connect-hopper-wizard-step2.component.html',
  styleUrls: ['./connect-hopper-wizard-step2.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', visibility: 'hidden' })),
      state('expanded', style({ height: '*', visibility: 'visible' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class ConnectHopperWizardStep2Component
  extends SelectableRow<HopperListingViewModel>
  implements OnInit, AfterViewInit
{
  @Output() prev = new EventEmitter()
  @Output() next = new EventEmitter()

  @Input() channel: Channel

  @ViewChild('paginator', { static: false }) paginator: MatPaginator
  @ViewChild(MatSort, { static: false }) sort: MatSort

  displayedColumns = ['rental_name', 'displayName', 'listingStatus', 'publishStatus', 'action']

  expandedElement: any

  isEmptyTable$ = isEmptyTable(this.dataSource)

  listings: HopperListingViewModel[]

  statusCtrl = new FormControl('')
  searchCtrl = new FormControl('')
  tagsCtrl = new FormControl([])
  citiesCtrl = new FormControl([])

  tags$ = this.store.pipe(select(selectRentalTags))
  cities$ = this.store.pipe(select(selectRentalCities))

  rentalTabs = RentalDialogTab

  searchFn = createSearch<HopperListingViewModel>(['rental_name', 'cancellationPolicyType', 'listingStatus'])

  validations: ListingsValidationFieldResult = {}

  validationsLoaded$ = this.store.pipe(select(selectHopperValidationsLoaded))

  refreshListings$ = new BehaviorSubject<boolean>(true)

  isExpansionDetailRow = (i: number, row: HopperListingViewModel & { detailRow?: boolean }) => {
    return row.hasOwnProperty('detailRow')
  }

  isCreatingListing = false
  constructor(
    private confirmDialog: ConfirmDialogService,
    private store: Store<any>,
    private validationFixHelper: RentalValidationFixHelperService,
    private actions: Actions,
    protected storage: UserStorage,
    private selectItemDialog: SelectItemDialogService,
    private bookingFormulasOverlay: BookingFormulasOverlayService
  ) {
    super(storage, TableType.VRBORentals)
  }

  ngOnInit() {
    this.onRefresh()

    this.actions
      .pipe(
        ofType(
          updateRentalComplete,
          updateRentalDetailsComplete,
          UpdateRoomComplete,
          UpsertRoomsComplete,
          addRentalImagesComplete,
          DeleteRoomComplete,
          updateRentalInstructionsComplete,
          updateRentalBaseRateComplete
        ),
        untilDestroy(this)
      )
      .subscribe(() => {
        this.store.dispatch(GetHopperRentalValidations())
      })

    this.actions.pipe(ofType(CreateHopperListing), untilDestroy(this)).subscribe(() => {
      this.isCreatingListing = true
    })

    this.actions.pipe(ofType(CreateHopperListingSuccess, ActionFailed), untilDestroy(this)).subscribe(() => {
      this.isCreatingListing = false
      this.store.dispatch(GetHopperListings())
    })

    this.actions
      .pipe(ofType(UpdateHopperListingSuccess, ActivateHopperListingSuccess), debounceTime(1000), untilDestroy(this))
      .subscribe(() => {
        this.store.dispatch(LoadConnectionsByChannel({ channelName: ChannelNameTokens.Hopper, channelId: '' }))
      })

    combineLatest([this.store.pipe(getHopperListings), this.store.pipe(select(selectHopperRentalValidations))])
      .pipe(untilDestroy(this))
      .subscribe(([listings, validations]) => {
        this.validations = validations
        this.listings = listings
        this.refreshListings$.next(true)
      })

    combineLatest([
      this.citiesCtrl.valueChanges.pipe(startWith('')),
      this.tagsCtrl.valueChanges.pipe(startWith([])),
      this.statusCtrl.valueChanges.pipe(startWith([])),
      this.searchCtrl.valueChanges.pipe(startWith([])),
      this.refreshListings$,
    ])
      .pipe(untilDestroy(this))
      .subscribe(([cities, tags, status, searchTerm]) => {
        let configs = this.listings
        switch (status) {
          case 'all':
            configs = this.listings
            break
          case 'active':
            configs = R.filter((listing) => !!listing.active, this.listings)
            break
          case 'inactive':
            configs = R.filter((listing) => !listing.active, this.listings)
            break
        }
        if (tags?.length > 0) {
          configs = R.filter((listing) => {
            return R.any(
              R.equals(true),
              R.map((tag) => R.contains(tag, listing.rental?.tags || []), tags)
            )
          }, configs)
        }
        if (cities?.length > 0) {
          configs = R.filter((listing) => R.includes(listing.rental?.address?.city || '', cities), configs)
        }
        configs = this.searchFn(configs, searchTerm)

        if (configs?.length !== this.dataSource.data?.length) {
          this.paginator?.firstPage()
        }
        this.dataSource.data = this.withExtraRow(configs || [], this.validations)
      })
  }

  onRefresh() {
    this.store.dispatch(GetHopperRentalValidations())
    this.store.dispatch(GetHopperListings())
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator
    this.dataSource.sort = this.sort
    this.dataSource.sortData = localeCompareSort
  }

  onCancelFilters() {
    this.statusCtrl.patchValue(null)
    this.tagsCtrl.patchValue(null)
    this.citiesCtrl.patchValue(null)
    this.searchCtrl.patchValue(null)
  }

  get isFiltering() {
    return this.statusCtrl.value || this.tagsCtrl.value || this.citiesCtrl.value || this.searchCtrl.value
  }

  withExtraRow(listings: HopperListingViewModel[], validations: ListingsValidationFieldResult) {
    const items = R.map((listing: HopperListingViewModel) => {
      const hasError = some(validations[listing._id], (t) => t.type === 'error')
      return {
        ...listing,
        detailRow: validations[listing._id]?.length > 0,
        hasError,
      }
    }, listings)

    return orderBy(items, [(listing) => (listing.active ? 1 : isNil(validations[listing._id]) ? -1 : 0)], ['asc'])
  }

  onComplete() {
    this.next.emit(true)
  }

  allInvalid() {
    return R.all((listing) => listing['detailRow'], this.dataSource.data)
  }

  setExpanded(row) {
    this.expandedElement = row
  }

  onToggleStatus(listing: HopperListingViewModel) {
    if (listing.active) {
      this.store.dispatch(DeactivateHopperListing({ listingId: listing.listingId, rentalId: listing._id }))
      this.store.dispatch(
        RemoveLocalConnectionsByIds({
          connectionIds: [getChannelConnectionId({ propertyId: listing.listingId, roomId: listing.listingId })],
        })
      )
    } else {
      this.store.dispatch(ActivateHopperListing({ listingId: listing.listingId, rentalId: listing._id }))
    }
  }

  onEditListing(listing: HopperListingViewModel) {
    this.selectItemDialog
      .open({
        title: $localize`Select cancellation policy`,
        help: $localize`Please select a cancellation policy for the listing.`,
        list: HopperCancellationPolicies,
        bindValue: 'value',
        bindLabel: 'label',
        bindTooltip: 'tootip',
        multiple: false,
        placeholder: $localize`Cancellation Policy`,
      })
      .pipe(filter((result) => !!result))
      .subscribe((policyId) => {
        this.store.dispatch(CreateHopperListing({ listing: { rentalId: listing._id, cancellationPolicy: policyId } }))
      })
  }

  onDeleteListing(listing: HopperListingViewModel) {
    this.confirmDialog
      .confirm({
        title: $localize`Delete listing?`,
        body: $localize`Are you sure you want to delete this listing?`,
      })
      .subscribe(() => {
        this.store.dispatch(DeleteHopperListing({ rentalId: listing._id }))
      })
  }

  onFixRental(listing: HopperListingViewModel, field: ListingValidationFieldResultItem) {
    this.validationFixHelper.onFixRental(listing.rental, field, this.validations[listing.rental.id])
  }

  onBookingFormula() {
    this.bookingFormulasOverlay.open(this.channel)
  }
}
